import { useRouter } from 'next/router';
import { useState } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import { getBreadCrumbSchema } from '../../../constants/componentStaticData';
import { DateFormatter } from '../../../utils/date-utils';
import { Breadcrumb, BREADCRUMB_TYPE } from '../../Atoms/BreadCrumb';
import { If } from '../../Atoms/Conditionals';
import { decodeHtml, stripUnwantedStyles } from '../../cms/layout/util';
import CMSPageDataServices from '../../cms/services/cms-pages.service';
import { Heading, HeadingType } from '../../Typography';
import { AuthorSideBio } from '../AuthorSideBio';
import { DOMAIN_CODE, MAX_PARAGRAPH_LIMIT } from '../constants';
import { IAUTHOR_PAGINATED_POSTS } from '../types';
import { extractParagraph } from '../utils/authorListUtils';

interface AUTHOR_DETAIL {
  authorName: string;
  data: IAUTHOR_PAGINATED_POSTS;
}

export const AuthorPostList = ({
  authorName,
  data,
}: AUTHOR_DETAIL): JSX.Element => {
  const [authorPostList, setAuthorPostList] = useState(data);
  const [page, setPage] = useState(1);
  const authorDetails = authorPostList.author_details;
  const router = useRouter();

  const BREADCRUMB: BREADCRUMB_TYPE[] = [
    { title: 'Home', link: '/' },
    { title: 'Authors', link: '/authors' },
    { title: authorName, link: '' },
  ];

  const handleViewMore = async () => {
    setPage((prevPage) => prevPage + 1);
    try {
      const res = await CMSPageDataServices.getAuthorPostsBySlug(
        authorPostList.author_details.slug,
        DOMAIN_CODE,
        page + 1
      );
      const dataRes = await res.data;
      setAuthorPostList({
        count: authorPostList.count,
        next: dataRes.next,
        previous: dataRes.previous,
        results: [...authorPostList.results, ...dataRes.results],
        author_details: { ...authorPostList.author_details },
      });
    } catch (err) {
      console.log('Error fetching all authors', err);
    }
  };

  return (
    <div>
      <div className="mt-4 pb-5 px-4">
        <Breadcrumb dataSet={BREADCRUMB} />

        <div className="hidden sm:block">
          <AuthorSideBio
            name={authorDetails.name}
            designation={authorDetails.designation}
            authorImg={authorDetails.image_url}
            description={authorDetails.description}
            linkedin={authorDetails.linkedin_url}
            facebook={authorDetails.facebook_url}
            twitter={authorDetails.twitter_url}
            slug={authorDetails.slug}
          />
          <hr />
        </div>

        <Heading
          content={`The latest articles by ${authorName}`}
          variant={HeadingType.h2}
          className="mt-4 text-s-40 sm:text-s-28"
        />
      </div>
      <hr />

      <div className="mt-4">
        {authorPostList.results?.map((article) => {
          const content = stripUnwantedStyles(article.content);
          const minsRead = Math.ceil(content.length / 1500);
          const updatedDate = DateFormatter(article.updated_at).format(
            'MMM Do, YYYY'
          );
          const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

          return (
            <div key={article.title}>
              <div
                onClick={() => router.push(`/s/${article.permalink}`)}
                key={article.title}
                className="hover:bg-blue-50 p-6 rounded-xl cursor-pointer sm:py-2 sm:px-4"
              >
                <div className="text-s-28 font-bold sm:text-s-20">
                  {decodeHtml(article.title)}
                </div>
                <div className="text-grey-500 mt-3 text-base sm:text-s-12">
                  Updated on {updatedDate} | {minsRead} min read
                </div>

                <div className="mt-8 sm:mt-4 sm:text-s-14">
                  <ResponsiveEllipsis
                    text={decodeHtml(
                      extractParagraph(content, MAX_PARAGRAPH_LIMIT)
                    )}
                    maxLine="3"
                    ellipsis="..."
                    trimRight
                    basedOn="words"
                    component="p"
                  />
                </div>
              </div>
              <hr className="m-4 sm:m-2" />
            </div>
          );
        })}

        <If condition={!!data.next}>
          <div
            className="text-s-14 font-semibold flex bg-[#EEF5FF] border border-[#8EBEFD] rounded-md w-1/4 text-blue-350 px-8 my-8 py-3 mx-auto justify-center items-center gap-2 cursor-pointer sm:w-full"
            onClick={handleViewMore}
          >
            View more
          </div>
        </If>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getBreadCrumbSchema(BREADCRUMB)),
        }}
      ></script>
    </div>
  );
};
